'use client';
import { Loader, Skeleton } from '@/components/ui';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarouselControls,
} from '@/components/ui/carousel';
import { EventDataType } from '@/types/EventDataType.type';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { EventCard } from './components';
import { useScreenWidth } from './hooks/useScreenWidth.hook';
import { Trackable } from '@/components/common';

const END_REACHED_THRESHOLD = 3;

interface EventCarouselProps {
  events: EventDataType[] | null;
  onEndReached?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
}

const EventCarousel = forwardRef(function EventCarousel(
  { events, onEndReached, hasMore, loadingMore }: EventCarouselProps,
  ref: React.Ref<{
    nextSlide: () => void;
    prevSlide: () => void;
  }>,
): React.ReactNode {
  const {
    setCarouselApi,
    nextSlide,
    prevSlide,
    currentSlide,
    totalSlides,
  } = useCarouselControls({ autoplay: true, autoplayInterval: 5000 });

  const screenWidth = useScreenWidth();
  const endReachedRef = useRef(false);

  useImperativeHandle(ref, () => ({
    nextSlide: () => nextSlide(),
    prevSlide: () => prevSlide(),
  }));

  useEffect(() => {
    if (!onEndReached || !hasMore || loadingMore || totalSlides === 0) return;

    const nearEnd = currentSlide >= Math.max(0, totalSlides - END_REACHED_THRESHOLD);

    if (nearEnd && !endReachedRef.current) {
      endReachedRef.current = true;
      onEndReached();
    } else if (!nearEnd) {
      endReachedRef.current = false;
    }
  }, [currentSlide, totalSlides, hasMore, loadingMore, onEndReached]);

  return (
    <Carousel
      opts={{ align: screenWidth > 640 ? 'start' : 'center', loop: true }}
      setApi={setCarouselApi}
    >
      {events !== null && (
        <CarouselContent className=''>
          {events.map((event, i) => {
            return (
              <CarouselItem
                key={`${event._id}_${i}`}
                className='w-[19.25rem] basis-auto py-2'
              >
                <Trackable
                  eventName='Click'
                  eventScope='Event Card (Carousel)'
                  eventProps={{
                    eventId: event._id,
                    eventName: event.name,
                  }}
                  className='h-full w-full px-0.5'
                >
                  <EventCard
                    className='h-full w-full'
                    event={event}
                  />
                </Trackable>
              </CarouselItem>
            );
          })}
          {loadingMore && (
            <CarouselItem className='flex w-[19.25rem] basis-auto items-center justify-center py-2'>
              <Loader />
            </CarouselItem>
          )}
        </CarouselContent>
      )}
      {!events && (
        <CarouselContent className='h-[20rem]'>
          {Array.from({ length: 6 }).map((_, i) => {
            return (
              <CarouselItem
                key={`eventCard_${i}`}
                className='w-[19rem] basis-auto py-2'
              >
                <Skeleton className='h-full w-full rounded-md' />;
              </CarouselItem>
            );
          })}
        </CarouselContent>
      )}
    </Carousel>
  );
});

export default EventCarousel;
